import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { patientApi, visitApi } from '../../api';
import { useToast } from '../../context/ToastContext';
import { formatAge, formatDate, formatDateTime, formatMoney, titleCase } from '../../utils/format';

const MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

/**
 * Take money against a patient's unpaid visits, and show every instalment ever
 * received. Loads its own copy of the record so it stays right even when opened
 * from a list that was fetched a while ago.
 */
export default function PaymentModal({ show, patientId, onHide, onChanged }) {
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openVisit, setOpenVisit] = useState(null);
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await patientApi.history(patientId);
      setData(res.data);
    } catch (err) {
      toast.error(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, toast]);

  useEffect(() => {
    if (show) {
      setOpenVisit(null);
      setAmount('');
      setNote('');
      setMode('cash');
      load();
    }
  }, [show, load]);

  const timeline = data?.timeline || [];

  const unpaid = timeline
    .filter((v) => v.status !== 'cancelled')
    .map((v) => ({ ...v, balance: Math.max(0, (v.fees?.total || 0) - (v.fees?.paid || 0)) }))
    .filter((v) => v.balance > 0)
    .sort((a, b) => new Date(a.visitDate) - new Date(b.visitDate));

  // Every instalment across every visit, newest first.
  const history = timeline
    .flatMap((v) => (v.payments || []).map((p) => ({ ...p, visitDate: v.visitDate })))
    .sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt));

  const due = data?.payment ? Math.max(0, data.payment.billed - data.payment.paid) : 0;

  function startCollecting(visit) {
    setOpenVisit(visit);
    setAmount(String(visit.balance));
    setNote('');
  }

  async function collect() {
    const value = Number(amount);
    if (!value || value <= 0) {
      toast.warn('Enter the amount received');
      return;
    }
    if (value > openVisit.balance) {
      toast.warn(`That is more than the ${formatMoney(openVisit.balance)} outstanding on this visit`);
      return;
    }

    setSaving(true);
    try {
      await visitApi.addPayment(openVisit._id, { amount: value, mode, note: note.trim() });
      toast.success(`${formatMoney(value)} received`);
      setOpenVisit(null);
      setAmount('');
      setNote('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  const patient = data?.patient;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h5">Payment</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: 'var(--clinic-primary)' }} />
          </div>
        ) : !patient ? (
          <Alert variant="danger" className="mb-0">
            Could not load this patient.
          </Alert>
        ) : (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
              <div>
                <div className="fw-bold fs-5 text-capitalize">{patient.name}</div>
                <div className="text-muted small">
                  {patient.patientId} &middot; {titleCase(patient.gender)}, {formatAge(patient)} &middot;{' '}
                  {patient.mobile}
                </div>
              </div>
              <div className="text-end">
                <div className={`fw-bold fs-4 ${due > 0 ? 'text-danger' : 'text-success'}`}>
                  {formatMoney(due)}
                </div>
                <div className="text-muted" style={{ fontSize: '0.76rem' }}>
                  {due > 0 ? 'still to pay' : 'nothing outstanding'}
                </div>
              </div>
            </div>

            {unpaid.length > 0 && (
              <>
                <div className="fw-semibold small mb-2">Visits with a balance</div>

                {unpaid.map((v) => (
                  <div key={v._id} className="pay-due-row flex-wrap">
                    <div className="flex-grow-1 min-w-0">
                      <div className="small fw-semibold">{formatDate(v.visitDate)}</div>
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        Charged {formatMoney(v.fees?.total || 0)} &middot; paid {formatMoney(v.fees?.paid || 0)}
                      </div>
                    </div>

                    {openVisit?._id === v._id ? null : (
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-danger fw-semibold">{formatMoney(v.balance)}</span>
                        <Button size="sm" className="btn-clinic" onClick={() => startCollecting(v)}>
                          Collect
                        </Button>
                      </div>
                    )}

                    {openVisit?._id === v._id && (
                      <div className="w-100 mt-3 pt-3 border-top">
                        <div className="d-flex flex-wrap gap-2 align-items-end">
                          <div>
                            <Form.Label className="mb-1">Amount</Form.Label>
                            <InputGroup style={{ width: 140 }}>
                              <InputGroup.Text>&#8377;</InputGroup.Text>
                              <Form.Control
                                autoFocus
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') collect();
                                  if (e.key === 'Escape') setOpenVisit(null);
                                }}
                              />
                            </InputGroup>
                          </div>

                          <div>
                            <Form.Label className="mb-1">Mode</Form.Label>
                            <Form.Select
                              value={mode}
                              onChange={(e) => setMode(e.target.value)}
                              style={{ width: 120 }}
                            >
                              {MODES.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>

                          <div className="flex-grow-1" style={{ minWidth: 150 }}>
                            <Form.Label className="mb-1">Note</Form.Label>
                            <Form.Control
                              value={note}
                              placeholder="optional"
                              onChange={(e) => setNote(e.target.value)}
                            />
                          </div>

                          <div className="d-flex gap-2">
                            <Button className="btn-clinic" disabled={saving} onClick={collect}>
                              {saving ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="bi bi-check-lg me-1" /> Take
                                </>
                              )}
                            </Button>
                            <Button variant="light" disabled={saving} onClick={() => setOpenVisit(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>

                        <div className="text-muted mt-2" style={{ fontSize: '0.76rem' }}>
                          Balance on this visit is {formatMoney(v.balance)}. Part payments are fine.
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            <div className="fw-semibold small mt-4 mb-2">
              Payment history
              <span className="text-muted fw-normal ms-2">{history.length} entr{history.length === 1 ? 'y' : 'ies'}</span>
            </div>

            {history.length === 0 ? (
              <div className="text-muted small">Nothing has been received from this patient yet.</div>
            ) : (
              <div className="pay-history">
                {history.map((p, i) => (
                  <div className="pay-history-row" key={p._id || i}>
                    <div className="pay-history-amount">+{formatMoney(p.amount)}</div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="small">
                        {formatDateTime(p.receivedAt)}
                        <Badge bg="secondary" className="ms-2 text-uppercase" style={{ fontSize: '0.62rem' }}>
                          {p.mode}
                        </Badge>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        For the visit of {formatDate(p.visitDate)}
                        {p.receivedBy?.name ? ` · taken by ${p.receivedBy.name}` : ''}
                        {p.note ? ` · ${p.note}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
